import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Button from "../../components/button";
import Timer from "../../components/timer";
import meteorBlue from "../../assets/images/meteor-blue.svg";
import star from "../../assets/images/star.svg";
import Alert from "../../components/alert";
import { setShowIntroJs, setScore, setData } from "../../redux/game24/reducer";
import { withRouter } from "react-router-dom";
import { Steps } from "intro.js-react";
import "intro.js/introjs.css";
import { isEqual } from "lodash";

const targetNumbers = [24, 48, 72, 96];
const operators = ["+", "-", "x", "÷"];
const correctAnswerScore = 10;
const scoreInterval = 15;
const changeColorSeconds = 90;
const gameStatuses = {
  CHOOSE_TARGET: "chooseTarget",
  PLAYING: "playing",
  CORRECT_ANSWER: "correctAnswer",
  INVALID_INPUT: "invalidInput",
};
const gameResultDefaults = {
  status: gameStatuses.CHOOSE_TARGET,
  starCount: 0,
  score: 0,
  answer: 0,
};

const _steps = [
  {
    element: ".chooseTargetNumber",
    intro: "You can choose the target number from the list.",
    position: "top",
  },
  {
    element: ".timer",
    intro:
      "You have unlimited time for each round and your score will be calculated depending on your solving time.",
    position: "left",
  },
  {
    element: ".user-input-buttons",
    intro:
      "You can use the mouse to make arithmetic operations with given numbers and operators.",
    position: "top",
  },
  {
    element: ".user-history-buttons",
    intro: "You can undo and redo your processes with the navigation buttons.",
    position: "top",
  },
  {
    element: ".unsolvedCount",
    intro: "You can see the number of remaining unsolved puzzles.",
    position: "bottom",
  },
  {
    intro: (
      <>
        Stars:
        <br />
        If your score is 9 or 10, you will see{" "}
        <img src={star} width="13" height="13" alt="star" />
        <img src={star} width="13" height="13" alt="star" className="ms-1" />
        <img src={star} width="13" height="13" alt="star" className="ms-1" />
        <br />
        If your score is 6,7 or 8, you will see{" "}
        <img src={star} width="13" height="13" alt="star" />
        <img src={star} width="13" height="13" alt="star" className="ms-1" />
        <br />
        If your score is 1 through 5, you will see{" "}
        <img src={star} width="13" height="13" alt="star" />
      </>
    ),
  },
];

const Single = (props) => {
  const dispatch = useDispatch();
  const inputRef = useRef();
  const { score, showIntroJs, data } = useSelector((state) => state.game24);
  const [userInput, setUserInput] = useState([]);
  const [userInputHistory, setUserInputHistory] = useState([]);
  const [activeUserInputHistory, setActiveUserInputHistory] = useState(-1);
  const [numbers, setNumbers] = useState([]);
  const [targetNumber, setTargetNumber] = useState(null);
  const [gameResult, setGameResult] = useState(gameResultDefaults);
  const [introJsSteps, setIntroJsSteps] = useState(null);
  const [introJsInitialStep, setIntroJsInitialStep] = useState(0);
  const [showRules, setShowRules] = useState(false);
  const [timerIsPlaying, setTimerIsPlaying] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerCounter, setTimerCounter] = useState(0);

  const handleToggleRules = () => {
    if (showRules) newGame();
    setShowRules(!showRules);
  };

  const handleShowIntroJs = () => {
    dispatch(setShowIntroJs(true));
  };

  const handleHideIntroJs = () => {
    dispatch(setShowIntroJs(false));
    setIntroJsSteps(null);
    newGame();
  };

  const introJsOnChange = (nextStepIndex, nextElement) => {
    if (nextStepIndex === 1 && targetNumber === null) {
      handleSetTarget(24)();
    }
  };

  const handleSetTarget = (number) => () => {
    setTargetNumber(number);
    setGameResult({
      ...gameResult,
      status: gameStatuses.PLAYING,
    });
  };

  useEffect(() => {
    if (showIntroJs && targetNumber === null) {
      setIntroJsInitialStep(0);
      setIntroJsSteps(_steps);
    } else if (
      showIntroJs &&
      numbers.length !== 0 &&
      targetNumber !== null &&
      userInput.length === 0
    ) {
      setTimerIsPlaying(false);
      setIntroJsInitialStep(1);
      setIntroJsSteps(_steps);
    }
    // eslint-disable-next-line
  }, [showIntroJs, numbers, userInput, targetNumber]);

  const generateNumbers = () => {
    if (targetNumber) {
      let _data = data["_" + targetNumber];
      let unsolvedData = _data.filter((d) => !d.solved);
      const dataIndex = Math.floor(Math.random() * unsolvedData.length);
      setNumbers(
        unsolvedData[dataIndex].numbers.map((number) => {
          return {
            value: number,
            text: number.toString(),
          };
        })
      );
    }
  };

  useEffect(() => {
    generateNumbers();
    // eslint-disable-next-line
  }, [targetNumber, data]);

  const newGame = () => {
    resetGame();
  };

  const continueToPlay = () => {
    if (
      gameResult.status === gameStatuses.INVALID_INPUT ||
      gameResult.status === gameStatuses.WRONG_ANSWER
    ) {
      setGameResult({
        ...gameResult,
        status: gameStatuses.PLAYING,
      });
    }
  };

  const resetGame = () => {
    if (targetNumber)
      setGameResult({
        ...gameResultDefaults,
        status: gameStatuses.PLAYING,
      });
    else setGameResult(gameResultDefaults);
    setUserInput([]);
    setUserInputHistory([]);
    setActiveUserInputHistory(-1);
    generateNumbers();
    setTimerCounter(timerCounter + 1);
    setTimerIsPlaying(true);
  };

  const solved = () => {
    let _data = [...data["_" + targetNumber]];
    const solvedIndex = _data.findIndex((d) =>
      isEqual(d.numbers, userInputHistory[0].oldNumbers)
    );
    const newValue = {
      ..._data[solvedIndex],
      solved: true,
    };
    _data.splice(solvedIndex, 1, newValue);
    dispatch(
      setData({
        ...data,
        ["_" + targetNumber]: _data,
      })
    );
  };

  const calculateScore = () => {
    const dif = Math.floor(timerSeconds / scoreInterval);
    const score = correctAnswerScore - dif;
    if (score < 1) return 1;
    return score;
  };

  const checkUserAnswer = (numbers) => {
    const _numbers = numbers.filter((num) => num.value > 0);
    if (_numbers.length === 1) {
      const userResult = _numbers[0].value;
      if (userResult === targetNumber) {
        solved();
        setTimerIsPlaying(false);
        const userScore = calculateScore();
        const starCount = userScore > 8 ? 3 : userScore > 5 ? 2 : 1;
        setGameResult({
          status: gameStatuses.CORRECT_ANSWER,
          score: userScore,
          starCount: starCount,
        });
        dispatch(setScore(score + userScore));
      }
    }
  };

  const loadData = () => {
    if (!data || data._24.length === 0) {
      const data24 = require("./data/24.json");
      const data48 = require("./data/48.json");
      const data72 = require("./data/72.json");
      const data96 = require("./data/96.json");
      dispatch(
        setData({
          _24: data24,
          _48: data48,
          _72: data72,
          _96: data96,
        })
      );
    }
  };

  useEffect(() => {
    resetGame();
    loadData();
    return () => {};
    // eslint-disable-next-line
  }, []);

  const timerOnChange = (seconds) => {
    setTimerSeconds(seconds);
  };

  const handleRemainingPuzzles = () => {
    if (targetNumber !== null) {
      return data["_" + targetNumber].filter((d) => !d.solved).length;
    }
    return "-";
  };

  const evalUserInput = (newInput) => {
    switch (userInput[1].input) {
      case "+":
        return userInput[0].input.value + newInput.input.value;
      case "-":
        return userInput[0].input.value - newInput.input.value;
      case "x":
        return userInput[0].input.value * newInput.input.value;
      case "÷":
        return userInput[0].input.value / newInput.input.value;
      default:
        return false;
    }
  };

  const handleNumberText = (_evalUserInput, newInput) => {
    if (Number.isInteger(_evalUserInput)) return _evalUserInput.toString();

    switch (userInput[1].input) {
      case "+":
        return `${userInput[0].input.text}+${newInput.input.text}`;
      case "-":
        return `${userInput[0].input.text}-${newInput.input.text}`;
      case "x":
        return `${userInput[0].input.text}*${newInput.input.text}`;
      case "÷":
        return `${userInput[0].input.text}/${newInput.input.text}`;
      default:
        return false;
    }
  };

  const handleSetUserInput = (input, inputType, index) => () => {
    const newInput = {
      input,
      inputType,
      index,
    };
    if (inputType === "number") {
      if (userInput.length <= 1) setUserInput([newInput]);
      else if (userInput.length === 2) {
        const _evalUserInput = evalUserInput(newInput);
        if (_evalUserInput !== false) {
          let newNumbers = [...numbers];
          newNumbers[userInput[0].index] = { value: 0, text: "0" };
          newNumbers[newInput.index] = {
            value: _evalUserInput,
            text: handleNumberText(_evalUserInput, newInput),
          };
          const nextActive = activeUserInputHistory + 1;
          let newUserInputHistory = userInputHistory.slice(0, nextActive);
          setUserInput([]);
          setUserInputHistory([
            ...newUserInputHistory,
            {
              oldNumbers: numbers,
              newNumbers,
            },
          ]);
          setActiveUserInputHistory(nextActive);
          setNumbers(newNumbers);
          checkUserAnswer(newNumbers);
        }
      }
    } else if (inputType === "operator") {
      if (userInput.length === 1) {
        setUserInput([...userInput, newInput]);
      } else if (userInput.length === 2) {
        let _userInput = [...userInput];
        _userInput.splice(1, 1);
        setUserInput([..._userInput, newInput]);
      }
    }
  };

  const isSelected = (input, inputType, index) => {
    if (
      userInput.find(
        (userInput) =>
          (userInput.input === input || userInput.input.value === input) &&
          userInput.inputType === inputType &&
          userInput.index === index
      )
    )
      return true;
    return false;
  };

  const handleUndo = () => {
    if (userInput.length === 2) {
      let _userInput = [...userInput];
      _userInput.splice(1, 1);
      setUserInput(_userInput);
    } else if (userInputHistory.length > 0 && activeUserInputHistory !== -1) {
      const history = userInputHistory[activeUserInputHistory];
      setNumbers(history.oldNumbers);
      setActiveUserInputHistory(activeUserInputHistory - 1);
    }
  };

  const handleRedo = () => {
    if (activeUserInputHistory < userInputHistory.length - 1) {
      const nextHistory = activeUserInputHistory + 1;
      const history = userInputHistory[nextHistory];
      setNumbers(history.newNumbers);
      setActiveUserInputHistory(nextHistory);
    }
  };

  const handleUndoDisabled = () => {
    if (userInput.length === 2) return false;
    else if (userInputHistory.length > 0 && activeUserInputHistory !== -1)
      return false;
    return true;
  };

  const handleRedoDisabled = () => {
    if (activeUserInputHistory < userInputHistory.length - 1) return false;
    return true;
  };

  return (
    <div className="game-play d-flex justify-content-center align-items-center align-self-center text-center h-100">
      <div className="header d-flex justify-content-between w-100">
        <div style={{ width: 180, textAlign: "left", display: "flex" }}>
          <div className="text-bg user-score" title="Score">
            <img src={star} width="24" height="24" alt="star" />
            {score}
          </div>
          <div
            className="text-bg user-score ms-1 unsolvedCount"
            title="Remaining Unsolved Puzzles"
          >
            <i className="fas fa-puzzle-piece"></i>
            {handleRemainingPuzzles()}
          </div>
        </div>
        <div className="title d-flex justify-content-center">
          <img src={meteorBlue} width="36" height="36" alt="meteor" />
          <h1 className="title">24k Game</h1>
        </div>
        <div style={{ width: 180 }}>
          {/* <Button to='/' className='me-2' title='Home Page'>
            <i className='fas fa-home'></i>
          </Button> */}
          <Button
            color="primary"
            className="me-2"
            title="Show Game Info"
            onClick={handleToggleRules}
          >
            <i className="fas fa-info"></i>
          </Button>
          <Button
            onClick={handleShowIntroJs}
            className="me-2"
            title="How to Play"
          >
            <i className="fas fa-question"></i>
          </Button>
          <Button to="/24-game" title="Close Game">
            <i className="fas fa-times"></i>
          </Button>
        </div>
      </div>

      <div className="play-area" ref={inputRef}>
        {targetNumber ? (
          <>
            <h3>Target Number</h3>
            <div className="d-inline-block text-bg">{targetNumber}</div>

            <div className="timer">
              <Timer
                counter={timerCounter}
                isPlaying={timerIsPlaying}
                onChange={timerOnChange}
                changeColorSeconds={changeColorSeconds}
              />
            </div>

            <div className="user-input-buttons">
              <div className="mt-2 mb-1 w-180 mx-auto">
                {numbers.map((number, index) => {
                  const selected = isSelected(number.value, "number", index);
                  return (
                    <Button
                      key={index}
                      className={
                        "btn-lg " +
                        (number.value === 0 ? " hidden" : "") +
                        (selected ? "btn-selected" : "")
                      }
                      disabled={selected}
                      onClick={handleSetUserInput(number, "number", index)}
                    >
                      {number.text}
                    </Button>
                  );
                })}
              </div>

              <div className="d-flex w-100 justify-content-center">
                {operators.map((operator, index) => {
                  const selected = isSelected(operator, "operator", index);
                  return (
                    <Button
                      color="secondary"
                      key={index}
                      className={selected ? "btn-selected" : ""}
                      disabled={selected}
                      onClick={handleSetUserInput(operator, "operator", index)}
                    >
                      {operator}
                    </Button>
                  );
                })}
              </div>
            </div>
            <div className="user-history-buttons">
              <div className="d-flex w-100 justify-content-center">
                <Button
                  color="secondary"
                  title="Undo"
                  disabled={handleUndoDisabled()}
                  onClick={handleUndo}
                >
                  <i className="fas fa-undo fa-sm" />
                </Button>
                <Button
                  color="secondary"
                  title="Redo"
                  disabled={handleRedoDisabled()}
                  onClick={handleRedo}
                >
                  <i className="fas fa-redo fa-sm" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="d-flex flex-column justify-content-center h-100">
            <div className="chooseTargetNumber">
              <h3>Choose Target Number</h3>
              <div>
                {targetNumbers.map((number) => (
                  <Button onClick={handleSetTarget(number)} key={number}>
                    {number}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        <Alert
          show={gameResult.status === gameStatuses.CORRECT_ANSWER}
          text={"Congratulations!"}
          starCount={gameResult.starCount}
          score={gameResult.score}
          buttons={[
            {
              text: "New Game!",
              onClick: newGame,
              color: "primary",
            },
          ]}
        />
        <Alert
          show={gameResult.status === gameStatuses.WRONG_ANSWER}
          text={"Oops...wrong answer!"}
          subText={"Your result: " + gameResult.answer}
          starCount={gameResult.starCount}
          score={gameResult.score}
          buttons={[
            {
              text: "Continue to Try!",
              onClick: continueToPlay,
              color: "primary",
            },
            {
              text: "New Game!",
              onClick: newGame,
              color: "primary",
            },
          ]}
        />
        <Alert
          show={gameResult.status === gameStatuses.INVALID_INPUT}
          text={"Oops...check your operations!"}
          starCount={gameResult.starCount}
          score={gameResult.score}
          buttons={[
            {
              text: "Continue",
              onClick: continueToPlay,
              color: "primary",
            },
          ]}
        />
        <Alert
          show={showRules}
          html={
            <div style={{ maxWidth: 500 }}>
              <h5>Objective</h5>
              <p>
                Your goal is to reach the "target number" using four basic
                operations and given numbers.
              </p>
              <h5>Rules</h5>
              <p>
                Use each number exactly once
                <br />
                You can only use the given operations +, -, x, /, and
                parentheses
                <br />
              </p>
              <h5>Scoring </h5>
              <p>
                10 pts if you can reach the target number in 15 seconds.
                <br />
                9 pts if you can reach the target number in 30 seconds etc.
                <br />
                You will get 1 point if you can get to the target number
                anytime.
              </p>
            </div>
          }
          buttons={[
            {
              text: "Close",
              onClick: handleToggleRules,
              color: "primary",
            },
          ]}
        />
      </div>

      {showIntroJs && introJsSteps !== null && (
        <Steps
          enabled={showIntroJs}
          steps={introJsSteps}
          initialStep={introJsInitialStep}
          onExit={handleHideIntroJs}
          options={{
            hidePrev: true,
            hideNext: true,
            doneLabel: "Done",
            nextToDone: true,
          }}
          onBeforeChange={introJsOnChange}
        />
      )}
    </div>
  );
};

export default withRouter(Single);
